import React, { startTransition, useCallback, useMemo, useRef, useState } from "react";
import { isEqual } from "lodash";
import dayjs from "dayjs";
import { COLORS, UI_DATE_FORMAT } from "../../../../consts";
import {
  isDisplayedLeafSizeWithinRange,
  roundLeafSizeForDisplay,
} from "./utils";

const useSpinachDashboardGroups = ({
  summaryForecastData,
  minimumIdealHarvestLeafSize,
  maximumIdealHarvestLeafSize,
  idealHarvestLeafSizePercentage,
  blockLabel,
  greenHouseLabel,
  selectedGreenhouses,
  selectedGreenhousesSet,
  fetchNewGreenhouseData,
}) => {
  const groupedGreenhousesRef = useRef({ 0: [], 1: [], 2: [], 3: [], 4: [] });
  const [expandedGroups, setExpandedGroups] = useState([]);

  const categorizeGreenhouses = useCallback((forecastData, minSize, maxSize) => {
    const groups = { 0: [], 1: [], 2: [], 3: [], 4: [] };

    Object.entries(forecastData).forEach(([ghName, rows]) => {
      // 1) Type 4 if all rows are harvested
      const allHarvested = rows.every(
        (r) => r.harvestDate && dayjs(r.harvestDate).isBefore(dayjs()),
      );
      if (allHarvested) {
        groups[4].push(ghName);
        return;
      }

      // We'll consider *all* unharvested rows:
      // const unharvested = rows.filter(
      //   (r) => !r.harvestDate || !dayjs(r.harvestDate).isBefore(dayjs()),
      // );

      const unharvested = rows;

      // 2) Type 0: all unharvested have current size > max
      const allType0 =
        unharvested.length > 0 &&
        unharvested.every((row) => {
          const current = row.lastData?.averagePercentile80_95 || 0;
          const displayedCurrent = roundLeafSizeForDisplay(current);
          return displayedCurrent !== null && displayedCurrent > maxSize;
        });
      if (allType0) {
        groups[0].push(ghName);
        return;
      }

      // rowIsType1 => "ready for harvest"
      const rowIsType1 = (row) => {
        const current = row.lastData?.averagePercentile80_95 || 0;
        if (isDisplayedLeafSizeWithinRange(current, minSize, maxSize))
          return true;
        const fDays = [row.day1, row.day2, row.day3, row.day4, row.day5].filter(
          (f) => typeof f === "object",
        );
        return fDays.some((f) => {
          const sum = current + (f.averagePercentile80_95 || 0);
          return isDisplayedLeafSizeWithinRange(sum, minSize, maxSize);
        });
      };

      // rowIsType2 => "mid stage"
      const rowIsType2 = (row) => {
        const current = row.lastData?.averagePercentile80_95 || 0;
        const displayedCurrent = roundLeafSizeForDisplay(current) ?? 0;
        const lowBound = 0.85 * minSize;
        if (displayedCurrent >= minSize) return false;
        const fDays = [row.day1, row.day2, row.day3, row.day4, row.day5].filter(
          (f) => typeof f === "object",
        );
        return fDays.some((f) => {
          const sum = current + (f.averagePercentile80_95 || 0);
          const displayedSum = roundLeafSizeForDisplay(sum) ?? 0;
          return displayedSum >= lowBound && displayedSum < minSize;
        });
      };

      // rowIsType3 => "early-mid"
      const rowIsType3 = (row) => {
        const current = row.lastData?.averagePercentile80_95 || 0;
        const displayedCurrent = roundLeafSizeForDisplay(current) ?? 0;
        const lowBound = 0.85 * minSize;
        if (displayedCurrent >= lowBound) return false;
        const fDays = [row.day1, row.day2, row.day3, row.day4, row.day5].filter(
          (f) => typeof f === "object",
        );
        return fDays.every((f) => {
          const sum = current + (f.averagePercentile80_95 || 0);
          const displayedSum = roundLeafSizeForDisplay(sum) ?? 0;
          return displayedSum < lowBound;
        });
      };

      // 3) Type 1 if ANY row belongs to type1
      const hasType1 = unharvested.some(rowIsType1);
      if (hasType1) {
        groups[1].push(ghName);
        return;
      }

      // 4) Type 2 if ANY row belongs to type2
      const hasType2 = unharvested.some(rowIsType2);
      if (hasType2) {
        groups[2].push(ghName);
        return;
      }

      // else => type 3
      groups[3].push(ghName);
    });

    return groups;
  }, []);

  const groupedGreenhouses = useMemo(() => {
    if (
      Object.keys(summaryForecastData).length === 0 ||
      !minimumIdealHarvestLeafSize ||
      !maximumIdealHarvestLeafSize
    ) {
      return groupedGreenhousesRef.current;
    }

    const next = categorizeGreenhouses(
      summaryForecastData,
      minimumIdealHarvestLeafSize,
      maximumIdealHarvestLeafSize,
    );

    if (!isEqual(groupedGreenhousesRef.current, next)) {
      groupedGreenhousesRef.current = next;
    }

    return groupedGreenhousesRef.current;
  }, [
    summaryForecastData,
    minimumIdealHarvestLeafSize,
    maximumIdealHarvestLeafSize,
    categorizeGreenhouses,
  ]);

  const groupConfig = {
    0: {
      label: "Exceeding Spec Size",
      color: COLORS.dangerRed,
      // description: `Leaf size exceeding ${idealHarvestLeafSize} cm`,
      description: "Leaf size exceeding spec size",
    },
    1: {
      label: "Ready for harvest",
      color: COLORS.successGreen,
      // description: `Leaf size reaching ${idealHarvestLeafSize} cm in next 5 days`,
      description: "Leaf size reaching ideal spec size in next 5 days",
    },
    2: {
      label: "Mid Stage",
      color: COLORS.primaryOrange,
      description: `Leaf size approaching ${idealHarvestLeafSizePercentage}% of spec size`,
    },
    3: {
      label: "Early-Mid Stage",
      color: COLORS.primaryBlue,
      description: "Leaf is in the growing stage",
    },
    4: {
      label: "Harvested",
      color: COLORS.darkGray,
      description: `All ${blockLabel}s harvested`,
    },
  };

  const filteredByType = useMemo(() => {
    const order = [1, 2, 0, 3, 4];
    const out = {};
    for (const type of order) {
      const all = groupedGreenhouses[type] || [];
      out[type] = all.filter((gh) => selectedGreenhousesSet.has(gh));
    }
    return out;
  }, [groupedGreenhouses, selectedGreenhousesSet]);

  const normalizeKeys = (keys) =>
    (Array.isArray(keys) ? keys : [keys]).map((k) => String(k));

  const handleGroupChange = (keys) => {
    const nextKeys = normalizeKeys(keys);
    const prevKeys = expandedGroups.map((k) => String(k));
    if (isEqual(nextKeys, prevKeys)) return;
    // figure out which groups just opened (for prefetch)
    const newlyOpened = nextKeys.filter((k) => !prevKeys.includes(k));
    // transition = makes the click feel instant while React renders content
    startTransition(() => setExpandedGroups(nextKeys));
    if (newlyOpened.length) {
      // greenhouse names in the groups we just opened
      const namesToPrefetch = newlyOpened.flatMap(
        (type) => filteredByType[type] || [],
      );
      // reuses your existing function; it's already guarded to only fetch "new"
      fetchNewGreenhouseData(namesToPrefetch);
    }
  };

  const findEarliestWithinRange = useCallback(
    (ghName, minSize, maxSize, forecastData) => {
      const rows = forecastData[ghName] || [];
      let earliest = null;
      rows.forEach((row) => {
        // skip harvested
        if (row.harvestDate && dayjs(row.harvestDate).isBefore(dayjs())) {
          return;
        }
        // check current
        const current = row.lastData?.averagePercentile80_95 || 0;
        if (isDisplayedLeafSizeWithinRange(current, minSize, maxSize)) {
          if (!earliest || dayjs().isBefore(earliest)) earliest = dayjs();
        } else {
          // check next 5 days
          const fDays = [row.day1, row.day2, row.day3, row.day4, row.day5].filter(
            (f) => typeof f === "object",
          );
          for (const fDay of fDays) {
            if (!fDay.date) continue;
            const sum = current + (fDay.averagePercentile80_95 || 0);
            const d = dayjs(fDay.date, UI_DATE_FORMAT);
            if (
              isDisplayedLeafSizeWithinRange(sum, minSize, maxSize) &&
              d.isValid()
            ) {
              if (!earliest || d.isBefore(earliest)) earliest = d;
              break;
            }
          }
        }
      });
      return earliest;
    },
    [],
  );

  const renderExceedList = () => {
    const group1 = (groupedGreenhouses[1] || []).filter((gh) =>
      selectedGreenhousesSet.has(gh),
    );
    if (!group1.length) return "None";

    const sorted = group1
      .map((ghName) => ({
        ghName,
        date: findEarliestWithinRange(
          ghName,
          minimumIdealHarvestLeafSize,
          maximumIdealHarvestLeafSize,
          summaryForecastData,
        ),
      }))
      .filter((x) => x.date)
      .sort((a, b) => a.date.valueOf() - b.date.valueOf());

    if (!sorted.length) return "None";

    const groups = sorted.reduce((acc, { ghName, date }) => {
      const d = date.format(UI_DATE_FORMAT);
      (acc[d] ||= []).push(ghName);
      return acc;
    }, {});
    const orderedDates = [
      ...new Set(sorted.map(({ date }) => date.format(UI_DATE_FORMAT))),
    ];

    return (
      <div className="!text-sm mt-2">
        {orderedDates.map((d) => (
          <div key={d}>
            <strong className="!font-semibold">
              {`${greenHouseLabel} ${groups[d].join(", ")}`}
            </strong>
            <span>&nbsp; - {d}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderMidStageList = () => {
    const list = (groupedGreenhouses[2] || []).filter((gh) =>
      selectedGreenhousesSet.has(gh),
    );
    return (
      <div className="!text-lg mt-2">
        {list.length ? (
          <strong className="!font-semibold">{`${greenHouseLabel} ${list.join(", ")}`}</strong>
        ) : (
          "None"
        )}
      </div>
    );
  };

  const renderEarlyMidStageList = () => {
    const list = (groupedGreenhouses[3] || []).filter((gh) =>
      selectedGreenhousesSet.has(gh),
    );
    return (
      <div className="!text-lg mt-2">
        {list.length ? (
          <strong className="!font-semibold">{`${greenHouseLabel} ${list.join(", ")}`}</strong>
        ) : (
          "None"
        )}
      </div>
    );
  };

  /* ---------- counts for the three stages -------------------------- */
  const group1All = groupedGreenhouses[1] || [];
  const type1Count = group1All.filter((gh) =>
    selectedGreenhousesSet.has(gh),
  ).length;

  const group2All = groupedGreenhouses[2] || [];
  const type2Count = group2All.filter((gh) =>
    selectedGreenhousesSet.has(gh),
  ).length;

  const group3All = groupedGreenhouses[3] || [];
  const type3Count = group3All.filter((gh) =>
    selectedGreenhousesSet.has(gh),
  ).length;

  const cardConfig = useMemo(
    () => ({
      ready: {
        title: "Ready for harvest",
        count: type1Count,
        color: COLORS.successGreen,
        content: renderExceedList(),
      },
      mid: {
        title: "Mid Stage",
        count: type2Count,
        color: COLORS.primaryOrange,
        content: renderMidStageList(),
      },
      early: {
        title: "Early‑Mid Stage",
        count: type3Count,
        color: COLORS.primaryBlue,
        content: renderEarlyMidStageList(),
      },
    }),
    [
      type1Count,
      type2Count,
      type3Count,
      groupedGreenhouses,
      selectedGreenhouses,
      summaryForecastData,
      greenHouseLabel,
    ],
  );

  return {
    groupedGreenhouses,
    expandedGroups,
    setExpandedGroups,
    type1Count,
    type2Count,
    type3Count,
    renderExceedList,
    renderMidStageList,
    renderEarlyMidStageList,
    handleGroupChange,
    filteredByType,
    groupConfig,
    cardConfig,
  };
};

export default useSpinachDashboardGroups;
